import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Loader2, Pencil, Trash2, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { pesos } from '../nomina/utils/nomina';
import { getSedesNomina } from '../nomina/Services/nomina.services';
import { METODOS_DISTRIBUCION } from '../nomina-directa/Services/nomina-directa.services';
import ParticipacionCentros from '../nomina-directa/components/ParticipacionCentros';
import {
  getPersonalAdministrativo,
  crearPersonalAdministrativo,
  actualizarPersonalAdministrativo,
  eliminarPersonalAdministrativo,
} from './Services/administrativo.services';

// Cargos sugeridos. El backend guarda texto libre, así que aparecer aquí no es obligatorio:
// la lista solo evita escribir siempre lo mismo.
const CARGOS = ['Gerente', 'Administrativo', 'Contabilidad', 'Asesor comercial', 'Talento humano', 'Otro'];

const vacio = () => ({
  cedula: '',
  nombre: '',
  apellido: '',
  correo: '',
  celular: '',
  cargo: 'Administrativo',
  area: '',
  salario: '',
  fechaIngreso: '',
  tipoNomina: 'Fijo',
  estado: 'Activo',
  observaciones: '',
  metodoDistribucion: 'equitativa',
  centros: [],
});

const desdeFicha = (p) => ({
  ...vacio(),
  ...Object.fromEntries(Object.entries(p || {}).map(([k, v]) => [k, v ?? ''])),
  salario: p?.salario ? String(p.salario) : '',
  metodoDistribucion: p?.metodoDistribucion || 'equitativa',
  centros: (p?.centros || []).map((c) => ({
    id: c.id,
    nombre: c.nombre || '',
    porcentaje: c.porcentaje ?? '',
  })),
});

/** "Por porcentajes · 2 centros" para la columna de la tabla. */
const resumenParticipacion = (p) => {
  const n = (p.centros || []).length;
  return `${METODOS_DISTRIBUCION[p.metodoDistribucion] || 'Equitativa'} · ${
    n ? `${n} centro${n === 1 ? '' : 's'}` : 'todos los de la nómina'
  }`;
};

const PersonalAdministrativoPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState('Activo');
  const [texto, setTexto] = useState('');
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(vacio());
  const [guardando, setGuardando] = useState(false);
  const [borrar, setBorrar] = useState(null);
  const [sedes, setSedes] = useState([]);

  const cargar = async () => {
    setLoading(true);
    try {
      setItems(await getPersonalAdministrativo({ estado }));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [estado]);

  useEffect(() => {
    getSedesNomina().then(setSedes).catch(() => setSedes([]));
  }, []);

  // El filtro de texto se aplica en el navegador: son pocas personas y así la lista
  // responde mientras se escribe, sin una consulta por tecla.
  const lista = useMemo(() => {
    const q = texto.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      `${p.nombre} ${p.apellido} ${p.cedula} ${p.cargo} ${p.area}`.toLowerCase().includes(q),
    );
  }, [items, texto]);

  const abrir = (persona) => {
    setEditando(persona || null);
    setForm(persona ? desdeFicha(persona) : vacio());
    setOpen(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (form.metodoDistribucion === 'porcentaje') {
      if (!form.centros.length) {
        toast({
          title: 'Participación',
          description: 'Para repartir por porcentajes elige los centros de esta persona.',
          variant: 'destructive',
        });
        return;
      }
      const suma = form.centros.reduce((s, c) => s + (Number(c.porcentaje) || 0), 0);
      if (Math.abs(suma - 100) > 0.01) {
        toast({
          title: 'Participación',
          description: `Los porcentajes suman ${suma}. Deben sumar 100.`,
          variant: 'destructive',
        });
        return;
      }
    }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        salario: Number(form.salario) || 0,
        centros: form.centros.map((c) => ({
          id: c.id,
          nombre: c.nombre,
          porcentaje: form.metodoDistribucion === 'porcentaje' ? Number(c.porcentaje) || 0 : null,
        })),
        creadoPor: { id: user?._id || user?.id, nombre: user?.nombre || user?.name || user?.email },
      };
      if (editando) {
        await actualizarPersonalAdministrativo(editando._id, payload);
        toast({ title: 'Persona actualizada' });
      } else {
        await crearPersonalAdministrativo(payload);
        toast({ title: 'Persona registrada' });
      }
      setOpen(false);
      cargar();
    } catch (err) {
      toast({ title: 'No se guardó', description: err.message, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    try {
      await eliminarPersonalAdministrativo(borrar._id);
      toast({ title: 'Persona eliminada' });
      setBorrar(null);
      cargar();
    } catch (err) {
      toast({ title: 'No se eliminó', description: err.message, variant: 'destructive' });
    }
  };

  const campo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 p-2.5 rounded-lg">
            <Briefcase className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Personal administrativo</h2>
            <p className="text-sm text-muted-foreground">
              Personas que no marcan en planta (administrativos, comerciales, gerencia). Su nómina se liquida
              aparte y se reparte entre los centros de lavado en «Nómina directa».
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Estado</Label>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Activo">Activos</SelectItem>
                  <SelectItem value="Inactivo">Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Buscar</Label>
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Nombre, documento, cargo o área"
                  className="pl-8"
                />
              </div>
            </div>
          </div>
          <Button onClick={() => abrir(null)}>
            <Plus className="mr-2 h-4 w-4" /> Registrar persona
          </Button>
        </div>

        <div className="rounded-lg border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Área</TableHead>
                <TableHead>Tipo nómina</TableHead>
                <TableHead>Participación</TableHead>
                <TableHead className="text-right">Salario</TableHead>
                <TableHead>Ingreso</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground">Cargando…</TableCell>
                </TableRow>
              ) : lista.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground">
                    Todavía no hay personal administrativo. Regístralo con «Registrar persona».
                  </TableCell>
                </TableRow>
              ) : (
                lista.map((p) => (
                  <TableRow key={p._id} className={p.tipoNomina === 'Temporal' ? 'bg-amber-50 dark:bg-amber-950' : ''}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {p.nombre} {p.apellido}
                      {p.correo && <div className="text-xs text-muted-foreground">{p.correo}</div>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{p.cedula}</TableCell>
                    <TableCell className="whitespace-nowrap">{p.cargo || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{p.area || '—'}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                          p.tipoNomina === 'Temporal'
                            ? 'bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {p.tipoNomina || 'Fijo'}
                      </span>
                    </TableCell>
                    <TableCell
                      className="whitespace-nowrap text-sm"
                      title={(p.centros || []).map((c) => `${c.nombre}${c.porcentaje ? ` · ${c.porcentaje} %` : ''}`).join('\n')}
                    >
                      {resumenParticipacion(p)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(p.salario)}</TableCell>
                    <TableCell className="whitespace-nowrap">{p.fechaIngreso || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={p.estado === 'Activo' ? 'default' : 'secondary'}>{p.estado}</Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" title="Editar" onClick={() => abrir(p)}>
                        <Pencil className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Eliminar" onClick={() => setBorrar(p)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !guardando && setOpen(v)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar persona' : 'Registrar persona'}</DialogTitle>
            <DialogDescription>
              El salario es el que usa la nómina directa para liquidar el periodo.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={guardar} className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pa-cedula">Documento *</Label>
                <Input id="pa-cedula" value={form.cedula} onChange={campo('cedula')} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-nombre">Nombre *</Label>
                <Input id="pa-nombre" value={form.nombre} onChange={campo('nombre')} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-apellido">Apellido</Label>
                <Input id="pa-apellido" value={form.apellido} onChange={campo('apellido')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-correo">Correo</Label>
                <Input id="pa-correo" type="email" value={form.correo} onChange={campo('correo')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-celular">Celular</Label>
                <Input id="pa-celular" value={form.celular} onChange={campo('celular')} />
              </div>
              <div className="space-y-2">
                <Label>Cargo</Label>
                <Select value={form.cargo} onValueChange={(v) => setForm({ ...form, cargo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CARGOS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-area">Área</Label>
                <Input id="pa-area" value={form.area} onChange={campo('area')} placeholder="Contabilidad, Comercial…" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-salario">Salario mensual *</Label>
                <Input id="pa-salario" type="number" min="0" value={form.salario} onChange={campo('salario')} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pa-ingreso">Fecha de ingreso</Label>
                <Input id="pa-ingreso" type="date" value={form.fechaIngreso} onChange={campo('fechaIngreso')} />
              </div>
              <div className="space-y-2">
                <Label>Tipo de nómina</Label>
                <Select value={form.tipoNomina} onValueChange={(v) => setForm({ ...form, tipoNomina: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fijo">Fijo</SelectItem>
                    <SelectItem value="Temporal">Temporal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Activo">Activo</SelectItem>
                    <SelectItem value="Inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pa-obs">Observaciones</Label>
              <Input id="pa-obs" value={form.observaciones} onChange={campo('observaciones')} />
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <div>
                <h4 className="text-sm font-semibold">Participación en los centros</h4>
                <p className="text-xs text-muted-foreground">
                  Cómo se reparte la nómina de esta persona entre las plantas. Cada nómina directa la copia y
                  queda congelada; ahí se puede ajustar sin tocar la ficha.
                </p>
              </div>
              <ParticipacionCentros
                metodo={form.metodoDistribucion}
                centros={form.centros}
                sedes={sedes}
                onChange={({ metodo, centros }) =>
                  setForm((f) => ({ ...f, metodoDistribucion: metodo, centros }))
                }
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={guardando}>
                Cancelar
              </Button>
              <Button type="submit" disabled={guardando}>
                {guardando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando…
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!borrar} onOpenChange={(v) => !v && setBorrar(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar persona</DialogTitle>
            <DialogDescription>
              Se borra la ficha de {borrar?.nombre} {borrar?.apellido}. Las nóminas directas ya calculadas
              conservan lo que se liquidó; al recalcular un borrador, la persona deja de aparecer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBorrar(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={eliminar}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default PersonalAdministrativoPage;
